import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Warenautomat kaufen“ (/warenautomat).
 * Eigener Winkel: Non-Food & Handgemachtes (Kunsthandwerk, Keramik, Geschenke,
 * Souvenirs) 24/7 unbeaufsichtigt – inkl. Abschnitt „individuell/nach Maß“
 * (0 Suchvolumen als eigenes KW, daher hier als Abschnitt). Referenz:
 * Geschenkeladen Ambiente Burgau (Live + Video, individuelle marktbox).
 */
@Component({
  selector: 'app-warenautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './warenautomat-page.component.html',
  styleUrls: ['../cluster-landing.shared.scss'],
})
export class WarenautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSlug = 'warenautomaten-fuer-non-food-artikel';

  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/ambiente-geschenkideen/U-MB-00319'
  );

  readonly videoThumb = 'https://i.ytimg.com/vi/-cB9s4rB5ro/hqdefault.jpg';
  videoOpen = false;
  videoUrl?: SafeResourceUrl;

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@waren.faq1.q:Welche Non-Food-Produkte kann ich verkaufen?`,
      a: $localize`:@@waren.faq1.a:Alles Nicht-Verderbliche: Kunsthandwerk, Keramik, Seifen, Souvenirs, Geschenkartikel, Merch, Bücher oder Deko. Da nichts gekühlt werden muss, ist der Warenautomat besonders vielseitig.`,
    },
    {
      q: $localize`:@@waren.faq2.q:Sind zerbrechliche oder hochwertige Waren sicher?`,
      a: $localize`:@@waren.faq2.a:Ja. Jedes Fach ist einzeln verschlossen (abschließbare Klappen, Diebstahlschutz) – die Ware ist geschützt und trotzdem rund um die Uhr verfügbar.`,
    },
    {
      q: $localize`:@@waren.faq3.q:Gibt es die marktbox auch individuell nach Maß?`,
      a: $localize`:@@waren.faq3.a:Die marktbox lässt sich mit Modellwahl (24/39 Fächer), Erweiterungen und Zubehör an dein Sortiment anpassen – bis hin zu individuellen Lösungen wie bei unserem Geschenkeladen-Partner.`,
    },
    {
      q: $localize`:@@waren.faq4.q:Warenautomat kaufen oder mieten?`,
      a: $localize`:@@waren.faq4.a:Beides: ab 6.800 € kaufen oder ab 300 € pro Monat mieten – je nach Modell und Zubehör.`,
    },
  ];

  openVideo(): void {
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube-nocookie.com/embed/-cB9s4rB5ro?autoplay=1&rel=0'
    );
    this.videoOpen = true;
  }

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@waren.metaTitle:Warenautomat für Non-Food & Geschenke kaufen | marktbox`;
    const description =
      $localize`:@@waren.metaDesc:Warenautomat für Non-Food kaufen oder mieten: Kunsthandwerk, Keramik & Geschenke 24/7 unbeaufsichtigt verkaufen. marktbox ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content: $localize`:@@waren.metaKeywords:Warenautomat, Non-Food Automat, Geschenkautomat, Kunsthandwerk Automat, Souvenirautomat, individueller Verkaufsautomat, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('warenautomat-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Warenautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('warenautomat-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    this.injectJsonLd('warenautomat-video-schema', {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: 'marktbox – Geschenkideen & 24/7-Verkauf (Ambiente Burgau)',
      description:
        'Wie ein Geschenkeladen mit einer individuellen marktbox Non-Food-Artikel rund um die Uhr verkauft – auch außerhalb der Öffnungszeiten.',
      thumbnailUrl: 'https://i.ytimg.com/vi/-cB9s4rB5ro/hqdefault.jpg',
      uploadDate: '2026-08-14',
      duration: 'PT3M23S',
      embedUrl: 'https://www.youtube-nocookie.com/embed/-cB9s4rB5ro',
      contentUrl: 'https://www.youtube.com/watch?v=-cB9s4rB5ro',
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/warenautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
